import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { Eye, EyeOff } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../utils/api';
import { callAuthPostWithFallback } from '../../utils/authFallback';

const LoginPage = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    rememberMe: false
  });

  const [isLoading, setIsLoading] = useState(false);
  const [twoFactorRequired, setTwoFactorRequired] = useState(false);
  const [twoFactorToken, setTwoFactorToken] = useState('');
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [twoFactorError, setTwoFactorError] = useState('');
  const [twoFactorLoading, setTwoFactorLoading] = useState(false);
  const [pendingUser, setPendingUser] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const { login, error, completeLoginSession } = useAuth();
  const navigate = useNavigate();
  const SANDBOX_API_BASE = 'https://zbxepuqat0.execute-api.eu-west-2.amazonaws.com/sandbox';
  const isProductionHost =
    typeof window !== 'undefined' &&
    window.location?.hostname &&
    window.location.hostname.includes('receevit.com') &&
    !window.location.hostname.includes('sandbox');

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    try {
      console.log('🔐 LoginPage: Form submitted - preventDefault called');
      e.preventDefault();
      console.log('🔐 LoginPage: Form data:', formData);
      setIsLoading(true);
      
      console.log('🔐 LoginPage: Calling login function');
      const result = await login(formData.email, formData.password);
      console.log('🔐 LoginPage: Login result:', result);

      if (result?.requiresTwoFactor) {
        console.log('🔐 LoginPage: Two-factor authentication required');
        setTwoFactorRequired(true);
        setTwoFactorToken(result.twoFactorToken || '');
        setPendingUser(result.user || { email: formData.email });
        setTwoFactorError('');
        return;
      }

      // Check if login was successful (handle both success: true and success: false with 'Login successful' message)
      if (result.success || (result.error && result.error.includes('Login successful'))) {
        console.log('🔐 LoginPage: Login successful, navigating...');
        console.log('🔐 LoginPage: User role:', result.user?.role);
        
        // If we have a user object, use it; otherwise try to get user from auth context
        const user = result.user || { role: 'user' }; // Default to user role if no user object
        
        // Navigate based on user role
        if (user.role === 'admin' || user.role === 'staff') {
          console.log('🔐 LoginPage: Navigating to /staff');
          navigate('/staff');
        } else {
          console.log('🔐 LoginPage: Navigating to /portal');
          navigate('/portal');
        }
      } else {
        console.log('🔐 LoginPage: Login failed:', result.error);
      }
    } catch (error) {
      console.error('🔐 LoginPage: Error in handleSubmit:', error);
      console.error('🔐 LoginPage: Error stack:', error.stack);
    } finally {
      setIsLoading(false);
      console.log('🔐 LoginPage: handleSubmit finished');
    }
  };

  const handleTwoFactorSubmit = async (e) => {
    e.preventDefault();

    if (!twoFactorToken) {
      setTwoFactorError('Two-factor token missing. Please try logging in again.');
      return;
    }

    const normalizedCode = String(twoFactorCode || '').replace(/\s+/g, '').trim();

    if (!normalizedCode || normalizedCode.length < 6) {
      setTwoFactorError('Please enter the 6-digit code from your authenticator app.');
      return;
    }

    try {
      setTwoFactorLoading(true);
      setTwoFactorError('');

      let response;

      try {
        const apiResponse = await api.post(
          '/api/users/two-factor/verify',
          {
            code: normalizedCode,
            twoFactorToken
          },
          {
            __skipAuthTokenPurge: true
          }
        );
        response = apiResponse.data;
      } catch (primaryError) {
        console.warn('🔐 LoginPage: API proxy 2FA verify failed, falling back to auth lambda', primaryError);
        response = await callAuthPostWithFallback(api, {
          apiPath: '/users/two-factor/verify',
          fallbackPath: '/users/two-factor/verify',
          payload: {
            code: normalizedCode,
            twoFactorToken
          },
          method: 'post',
          authMode: 'query',
          requireAuthToken: false,
          forceFallback: true,
          allowFallbackOnProduction: true,
          customQuery: {
            authToken: twoFactorToken
          }
        });
      }

      if (response.success && response.token && response.user) {
        window.dispatchEvent(new CustomEvent('twofactor:updated'));
        if (!isProductionHost) {
          localStorage.setItem('__sandboxApiBaseOverride', SANDBOX_API_BASE);
        } else {
          localStorage.removeItem('__sandboxApiBaseOverride');
        }
        const validatedUser = await completeLoginSession({
          token: response.token,
          userData: response.user,
          loginEmail: pendingUser?.email
        });

        setTwoFactorRequired(false);
        setTwoFactorToken('');
        setTwoFactorCode('');
        setPendingUser(null);

        if (validatedUser.role === 'admin' || validatedUser.role === 'staff') {
          navigate('/staff');
        } else {
          navigate('/portal');
        }
      } else {
        setTwoFactorError(response.error || response.message || 'Verification failed. Please try again.');
      }
    } catch (err) {
      console.error('🔐 LoginPage: Two-factor verification failed:', err);
      const message = err.response?.data?.error || err.response?.data?.message || 'Verification failed. Please try again.';
      setTwoFactorError(message);
    } finally {
      setTwoFactorLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Login | Receevit®</title>
        <meta name="description" content="Log in to your Receevit® account to manage invoices, track payments, and control your B2B credit." />
        <link rel="canonical" href="https://www.receevit.com/login" />
        <meta property="og:title" content="Login | Receevit®" />
        <meta property="og:description" content="Log in to your Receevit® account to manage invoices, track payments, and control your B2B credit." />
        <meta property="og:url" content="https://www.receevit.com/login" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://www.receevit.com/og-image.png" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            "name": "Login | Receevit®",
            "url": "https://www.receevit.com/login",
            "description": "Log in to your Receevit® account to manage invoices, track payments, and control your B2B credit.",
            "publisher": {
              "@type": "Organization",
              "name": "Receevit®",
              "url": "https://www.receevit.com/"
            }
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              {
                "@type": "ListItem",
                "position": 1,
                "name": "Home",
                "item": "https://www.receevit.com/"
              },
              {
                "@type": "ListItem",
                "position": 2,
                "name": "Login",
                "item": "https://www.receevit.com/login"
              }
            ]
          })}
        </script>
      </Helmet>

      <div className="flex flex-col items-center justify-center py-10 px-4 sm:px-6 lg:px-8 w-full max-w-7xl mx-auto flex-1">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45 }}
          className="w-full max-w-md space-y-6"
        >
          <div className="text-center">
            <h1 className="text-3xl font-bold text-white mb-2">Sign in</h1>
            <p className="text-blue-100 text-sm">
              Client portal — invoices, payments, and settings
            </p>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45, delay: 0.05 }}
            className="bg-white rounded-3xl shadow-2xl p-8"
          >
            <div className="text-center mb-8">
              <p className="text-gray-600 text-sm">
                Don&apos;t have an account?{' '}
                <Link
                  to="/signup"
                  className="text-[#1F11ED] hover:text-blue-700 font-medium transition-colors duration-200"
                >
                  Sign up
                </Link>
              </p>
            </div>

            {error && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl text-red-800">
                {error}
              </div>
            )}

            {twoFactorRequired ? (
              <form onSubmit={handleTwoFactorSubmit} className="space-y-6">
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-blue-800">
                  <p className="font-semibold">Two-factor authentication required</p>
                  <p className="text-sm mt-1">
                    Open your authenticator app and enter the 6-digit code to finish signing in. You can also use one of your backup codes.
                  </p>
                </div>

                <div>
                  <label htmlFor="twoFactorCode" className="block text-sm font-medium text-gray-900 mb-2">
                    6-digit code
                  </label>
                  <input
                    type="text"
                    id="twoFactorCode"
                    name="twoFactorCode"
                    value={twoFactorCode}
                    onChange={(e) => setTwoFactorCode(e.target.value.replace(/[^0-9A-Za-z-]/g, ''))}
                    required
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-300 rounded-xl text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#1F11ED] focus:border-transparent transition-all duration-200 tracking-widest text-center text-lg"
                    placeholder="••••••"
                  />
                  {twoFactorError && (
                    <p className="mt-2 text-sm text-red-600">{twoFactorError}</p>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={twoFactorLoading}
                  className="w-full bg-[#1F11ED] text-white py-3 px-6 rounded-xl font-semibold hover:bg-blue-700 hover:shadow-lg transition-all duration-300 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                >
                  {twoFactorLoading ? 'Verifying...' : 'Verify & Continue'}
                </button>

                <button
                  type="button"
                  onClick={() => {
                    setTwoFactorRequired(false);
                    setTwoFactorToken('');
                    setTwoFactorCode('');
                    setTwoFactorError('');
                  }}
                  className="w-full text-sm text-gray-500 hover:text-gray-700 underline"
                >
                  Start over
                </button>
              </form>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-900 mb-2">
                    Email Address
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-300 rounded-xl text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#1F11ED] focus:border-transparent transition-all duration-200"
                    placeholder="Enter your email address"
                  />
                </div>

                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-gray-900 mb-2">
                    Password
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      id="password"
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 pr-12 bg-gray-50 border border-gray-300 rounded-xl text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#1F11ED] focus:border-transparent transition-all duration-200"
                      placeholder="Enter your password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(prev => !prev)}
                      className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500 hover:text-gray-700 focus:outline-none"
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <input
                      type="checkbox"
                      id="remember"
                      name="rememberMe"
                      checked={formData.rememberMe}
                      onChange={handleChange}
                      className="w-4 h-4 text-[#1F11ED] bg-gray-50 border-gray-300 rounded focus:ring-[#1F11ED] focus:ring-2"
                    />
                    <label htmlFor="remember" className="text-sm text-gray-700">
                      Remember me
                    </label>
                  </div>
                  <Link
                    to="/forgot-password"
                    className="text-sm text-[#1F11ED] hover:text-blue-700 transition-colors duration-200"
                  >
                    Forgot password?
                  </Link>
                </div>

                <button
                  type="submit"
                  disabled={isLoading}
                  onClick={(e) => {
                    console.log('🔐 LoginPage: Button clicked');
                    console.log('🔐 LoginPage: Event type:', e.type);
                    console.log('🔐 LoginPage: Event target:', e.target);
                  }}
                  className="w-full bg-[#1F11ED] text-white py-3 px-6 rounded-xl font-semibold hover:bg-blue-700 hover:shadow-lg transition-all duration-300 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                >
                  {isLoading ? 'Signing In...' : 'Sign In'}
                </button>
              </form>
            )}

          </motion.div>
        </motion.div>
      </div>
    </>
  );
};

export default LoginPage;